import {
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@common/components/ui/form"
import { Textarea } from "@common/components/ui/textarea"
import { useAutosizeTextArea } from "@common/hooks/use-autosize-textarea"
import { cn } from "@common/lib/utils"
import type { ReactNode, TextareaHTMLAttributes } from "react"
import { useRef } from "react"
import type { ControllerRenderProps, FieldValues, Path } from "react-hook-form"
import { useFormContext } from "react-hook-form"
import { FieldLabel } from "./label"

type BareProps<T extends FieldValues> = Omit<
  TextareaHTMLAttributes<HTMLTextAreaElement>,
  "name" | "value" | "defaultValue" | "onChange"
> & {
  name: Path<T>
  optional?: boolean
  autosize?: boolean
  maxHeight?: number
  dictation?: boolean
}

export function BareTextareaField<T extends FieldValues>({
  name,
  ...rest
}: BareProps<T>) {
  const { control } = useFormContext<T>()
  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => <TextareaControl<T> field={field} {...rest} />}
    />
  )
}

type ControlProps<T extends FieldValues> = Omit<BareProps<T>, "name"> & {
  field: ControllerRenderProps<T, Path<T>>
}

function TextareaControl<T extends FieldValues>({
  autosize,
  ...rest
}: ControlProps<T>) {
  return autosize ? (
    <AutosizeTextarea<T> {...rest} />
  ) : (
    <PlainTextarea<T> {...rest} />
  )
}

function PlainTextarea<T extends FieldValues>({
  field,
  optional,
  maxHeight: _maxHeight,
  onBlur,
  ...rest
}: Omit<ControlProps<T>, "autosize">) {
  return (
    <Textarea
      aria-required={!optional}
      {...rest}
      {...field}
      value={field.value ?? ""}
      onBlur={(event) => {
        field.onBlur()
        onBlur?.(event)
      }}
    />
  )
}

function AutosizeTextarea<T extends FieldValues>({
  field,
  optional,
  maxHeight,
  className,
  onBlur,
  ...rest
}: Omit<ControlProps<T>, "autosize">) {
  const ref = useRef<HTMLTextAreaElement | null>(null)
  const value = field.value ?? ""
  useAutosizeTextArea({ ref, maxHeight, dependencies: [value] })
  const { ref: fieldRef, ...fieldProps } = field
  return (
    <Textarea
      aria-required={!optional}
      className={cn("resize-none", className)}
      {...rest}
      {...fieldProps}
      ref={(node) => {
        ref.current = node
        fieldRef(node)
      }}
      value={value}
      onBlur={(event) => {
        field.onBlur()
        onBlur?.(event)
      }}
    />
  )
}

type Props<T extends FieldValues> = BareProps<T> & {
  label?: ReactNode
  labelClassName?: string
  textareaClassName?: string
  messageClassName?: string
}

export function TextareaField<T extends FieldValues>({
  name,
  label,
  className,
  labelClassName,
  textareaClassName,
  messageClassName,
  optional,
  ...rest
}: Props<T>) {
  const { control } = useFormContext<T>()
  return (
    <FormField
      control={control}
      name={name}
      render={() => (
        <FormItem className={className}>
          {label !== undefined && (
            <FieldLabel
              label={label}
              optional={optional}
              className={labelClassName}
            />
          )}
          <FormControl>
            <BareTextareaField<T>
              name={name}
              optional={optional}
              className={textareaClassName}
              {...rest}
            />
          </FormControl>
          <FormMessage className={messageClassName} />
        </FormItem>
      )}
    />
  )
}
